import { useEffect, useLayoutEffect, useState } from 'react';
import PropTypes from 'prop-types';
import InfiniteScroll from 'react-infinite-scroller';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  Box,
} from '@mui/material';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import { TbMoodEmpty } from 'react-icons/tb';

import { ReactComponent as SortableIcon } from '../../assets/images/Sortable.svg';
import { ReactComponent as BigArrowUpIcon } from '../../assets/images/BigArrowUp.svg';
import { ReactComponent as BigArrowDownIcon } from '../../assets/images/BigArrowDown.svg';

import { constants, Utils } from '../../helpers/';

import './CustomTable.scss';

const CustomTable = ({
  colsDef = [],
  colSorting,
  rowData = [],
  cssClasses,
  rowClassName,
  rowClickDisabled = false,
  rowClickHandler,
  specialDataField,
  specialColumnType,
  sortableField = null,
  noDataContent,
  dark = false,
  enableInfiniteScroll = false,
  recordsPerPage = 100,
}) => {
  const [columns, setColumns] = useState([...colsDef]);
  const [rows, setRows] = useState(Array.isArray(rowData) ? [...rowData] : []);
  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState('asc');
  const [overflowStates, setOverflowStates] = useState({});
  const [displayedRows, setDisplayedRows] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);

  const defaultNoDataContent = (
    <Box className="no-data-container">
      <TbMoodEmpty size={32} className="no-data-icon" />
      <p className="no-data-alert">{constants.NO_MATCHING_DATA_FOUND_TEXT}</p>
    </Box>
  );

  // Sort function that can be reused
  const getSortedRows = (dataRows, sortBy, sortOrder) => {
    return [...dataRows].sort((a, b) => {
      let fieldToSort = sortableField ? `sortable_${sortBy}` : sortBy;
      let aValue = a[fieldToSort];
      let bValue = b[fieldToSort];

      // Handle special column types
      if (specialColumnType && sortBy === specialDataField) {
        aValue = a[sortBy]?.props?.children[1];
        bValue = b[sortBy]?.props?.children[1];
      }

      // Handle null/undefined values
      if (!aValue) aValue = '';
      if (!bValue) bValue = '';

      // Convert to lowercase for string comparison
      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      // Compare values
      if (aValue < bValue) {
        return sortOrder === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortOrder === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  useEffect(() => {
    setColumns(Array.isArray(colsDef) ? [...colsDef] : []);
    setRows(Array.isArray(rowData) ? [...rowData] : []);
  }, [rowData, colsDef]);

  // Initialize displayed rows when rows data changes
  useEffect(() => {
    if (enableInfiniteScroll && rows.length > 0) {
      const sortedRows = orderBy ? getSortedRows(rows, orderBy, order) : rows;
      setDisplayedRows(sortedRows.slice(0, recordsPerPage));
      setHasMore(sortedRows.length > recordsPerPage);
      setPage(1);
    } else if (enableInfiniteScroll) {
      setDisplayedRows([]);
      setHasMore(false);
    }
  }, [rows, orderBy, order, enableInfiniteScroll, recordsPerPage]);

  // Check for text overflow after component mounts and when data changes
  useEffect(() => {
    if (rows.length === 0 || columns.length === 0) return;

    const checkOverflow = () => {
      const newOverflowStates = {};

      // Use displayedRows for infinite scrolling, otherwise use rows
      const dataToCheck = enableInfiniteScroll ? displayedRows : rows;

      // Check each cell for overflow
      dataToCheck.forEach((row, rowIndex) => {
        columns.forEach((col, colIndex) => {
          const cellId = `cell${rowIndex}${colIndex}`;
          const cellElement = document.querySelector(`#${cellId}`);

          if (cellElement) {
            const textDiv = cellElement.querySelector('div');
            if (textDiv) {
              const hasOverflow =
                textDiv.scrollWidth > textDiv.clientWidth ||
                textDiv.scrollHeight > textDiv.clientHeight;
              newOverflowStates[cellId] = hasOverflow;
            }
          }
        });
      });

      setOverflowStates(newOverflowStates);

      // Debug: Log overflow states
      Utils.vmsLogger().log('CustomTable overflow states:', newOverflowStates);
    };

    // Use setTimeout to ensure DOM is ready
    const timeoutId = setTimeout(checkOverflow, 100);
    return () => clearTimeout(timeoutId);
  }, [rows, displayedRows, columns, enableInfiniteScroll]);

  // Add resize listener to recheck overflow when window resizes
  useEffect(() => {
    if (rows.length === 0 || columns.length === 0) return;

    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const newOverflowStates = {};

        // Use displayedRows for infinite scrolling, otherwise use rows
        const dataToCheck = enableInfiniteScroll ? displayedRows : rows;

        dataToCheck.forEach((row, rowIndex) => {
          columns.forEach((col, colIndex) => {
            const cellId = `cell${rowIndex}${colIndex}`;
            const cellElement = document.querySelector(`#${cellId}`);

            if (cellElement) {
              const textDiv = cellElement.querySelector('div');
              if (textDiv) {
                const hasOverflow =
                  textDiv.scrollWidth > textDiv.clientWidth ||
                  textDiv.scrollHeight > textDiv.clientHeight;
                newOverflowStates[cellId] = hasOverflow;
              }
            }
          });
        });

        setOverflowStates(newOverflowStates);

        // Debug: Log overflow states on resize
        Utils.vmsLogger().log(
          'CustomTable overflow states (resize):',
          newOverflowStates,
        );
      }, 150); // Debounce for 150ms
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, [rows, displayedRows, columns, enableInfiniteScroll]);

  // When the browser window is resized, we rehydrate the columns and rows states
  // so that the table is re-rendered to determine the list of popovers to enable
  // dynamically.
  useLayoutEffect(() => {
    const rehydrate = () => {
      setColumns(Array.isArray(colsDef) ? [...colsDef] : []);
      setRows(Array.isArray(rowData) ? [...rowData] : []);
    };

    window.addEventListener('resize', rehydrate);

    return () => window.removeEventListener('resize', rehydrate);
  }, [colsDef, rowData]);

  const handleRequestSort = (field, columnType) => {
    if (!field) return;

    const isAsc = orderBy === field && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';

    setOrderBy(field);
    setOrder(newOrder);

    if (enableInfiniteScroll) {
      // Sort the full dataset and update displayed rows for infinite scrolling
      const sortedRows = getSortedRows(rows, field, newOrder);
      setDisplayedRows(sortedRows.slice(0, recordsPerPage));
      setPage(1);
      setHasMore(sortedRows.length > recordsPerPage);
    } else {
      // Original sorting logic for non-infinite scrolling
      const sortedRows = getSortedRows(rows, field, newOrder);
      setRows(sortedRows);
    }
  };

  const getSortIcon = (columnId) => {
    if (orderBy !== columnId) {
      return (
        <SortableIcon
          color={dark ? 'var(--hGreyscale_72)' : 'var(--greyscale_40)'}
          style={{
            minHeight: '16px',
            minWidth: '16px',
            height: '16px',
            width: '16px',
          }}
        />
      );
    }
    return order === 'asc' ? (
      <BigArrowUpIcon
        color={dark ? 'var(--hGreyscale_72)' : 'var(--greyscale_40)'}
        style={{
          minHeight: '16px',
          minWidth: '16px',
          height: '16px',
          width: '16px',
        }}
      />
    ) : (
      <BigArrowDownIcon
        color={dark ? 'var(--hGreyscale_72)' : 'var(--greyscale_40)'}
        style={{
          minHeight: '16px',
          minWidth: '16px',
          height: '16px',
          width: '16px',
        }}
      />
    );
  };

  const loadMore = () => {
    if (!enableInfiniteScroll) return;

    const nextPage = page + 1;
    const endIndex = nextPage * recordsPerPage;

    // Sort the full dataset if there's an active sort
    const sortedRows = orderBy ? getSortedRows(rows, orderBy, order) : rows;
    const newRows = sortedRows.slice(0, endIndex);

    setDisplayedRows(newRows);
    setPage(nextPage);
    setHasMore(endIndex < sortedRows.length);
  };

  const renderHeader = () => {
    if (!Array.isArray(columns)) {
      return null;
    }

    return (
      <TableHead className="custom-table-header">
        <TableRow>
          {columns.map((col) => (
            <TableCell
              key={`header${col?.field}`}
              sortDirection={orderBy === col?.field ? order : false}
              className="custom-table-header-cell"
              align={col?.field?.toUpperCase() === 'ACTION' ? 'center' : 'left'}
              sx={{
                width: col?.cellMaxWidth,
                maxWidth: col?.cellMaxWidth,
              }}
            >
              {colSorting === true && col?.field?.toUpperCase() !== 'ACTION' ? (
                <TableSortLabel
                  className={`table-header-cell-sort-label ${orderBy === col?.field ? 'active' : ''}`}
                  active={orderBy === col?.field}
                  direction={orderBy === col?.field ? order : 'asc'}
                  onClick={() => {
                    if (col?.field?.toUpperCase() === specialDataField) {
                      handleRequestSort(col?.field, specialColumnType);
                    } else {
                      handleRequestSort(col?.field, null);
                    }
                  }}
                  IconComponent={() => getSortIcon(col?.field)}
                >
                  {col?.displayName}
                </TableSortLabel>
              ) : (
                <div
                  className="column-name"
                  style={{
                    textAlign:
                      col?.field?.toUpperCase() !== 'ACTION'
                        ? 'left'
                        : 'center',
                  }}
                  onClick={col?.clickHandler}
                >
                  {col?.displayName}
                </div>
              )}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  };

  const renderRow = (row, rowIndex) => {
    if (!Array.isArray(columns)) return null;

    return (
      <TableRow
        key={`row${rowIndex}`}
        className={`row-item ${rowClassName ? rowClassName : ''}`}
        onClick={(e) => {
          e?.preventDefault();
          e?.stopPropagation();

          if (!rowClickDisabled && rowClickHandler) {
            rowClickHandler(row.rowId);
          }
        }}
        hover
      >
        {columns.map((col, colIndex) => {
          const cellId = `cell${rowIndex}${colIndex}`;
          const cellContent =
            col?.field === 'fileName' &&
            row['sortable_incidentNoteType'] === 'COMMENT' &&
            row['fileName'] === ''
              ? constants.INCIDENTS_EVIDENCE_EVIDENCE_TYPE_COMMENT
              : row[col?.field];

          return (
            <TableCell
              key={cellId}
              id={cellId}
              className="custom-table-body-cell"
              align={col?.field?.toUpperCase() === 'ACTION' ? 'center' : 'left'}
              style={{
                width: col?.cellMaxWidth,
                maxWidth: col?.cellMaxWidth,
              }}
            >
              {col?.field?.toUpperCase() !== 'ACTION' &&
              col?.field?.toUpperCase() !== 'STATUS' &&
              cellContent ? (
                overflowStates[cellId] ? (
                  <OverlayTrigger
                    placement="top"
                    delay={{ show: 250, hide: 400 }}
                    overlay={
                      <Tooltip
                        className={dark ? 'light-tooltip' : ''}
                        id={`tooltip-cell-${rowIndex}-${col?.field}`}
                      >
                        {col?.field === 'fileName' &&
                        row['sortable_incidentNoteType'] === 'COMMENT' &&
                        row['fileName'] === ''
                          ? 'Comment'
                          : row[col?.field]}
                      </Tooltip>
                    }
                  >
                    <div
                      style={{
                        maxWidth: col?.cellMaxWidth,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {cellContent}
                    </div>
                  </OverlayTrigger>
                ) : (
                  <div
                    style={{
                      maxWidth: col?.cellMaxWidth,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {cellContent}
                  </div>
                )
              ) : (
                <div>{cellContent}</div>
              )}
            </TableCell>
          );
        })}
      </TableRow>
    );
  };

  return (
    <Box className={`custom-table-wrapper ${dark ? 'dark' : ''}`}>
      {enableInfiniteScroll ? (
        <InfiniteScroll
          pageStart={1}
          loadMore={loadMore}
          hasMore={hasMore}
          loader={
            <div className="loader" key={0}>
              {constants.CUSTOM_TABLE_LOADING}
            </div>
          }
          useWindow={true}
          threshold={100}
        >
          <TableContainer component={Paper} className="custom-table-container">
            <Table
              className={`${dark ? 'sortable-custom-table-dark' : 'sortable-custom-table'} ${cssClasses}`}
            >
              {renderHeader()}
              <TableBody className="custom-table-body">
                {displayedRows.length > 0 ? (
                  displayedRows.map((row, rowIndex) => renderRow(row, rowIndex))
                ) : (
                  <TableRow className="row-item row-item-disabled">
                    <TableCell
                      className="custom-table-body-cell"
                      colSpan={columns.length}
                      align="center"
                      sx={{ padding: '8px 0px' }}
                    >
                      {noDataContent || defaultNoDataContent}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </InfiniteScroll>
      ) : (
        <TableContainer component={Paper} className="custom-table-container">
          <Table
            className={`${dark ? 'sortable-custom-table-dark' : 'sortable-custom-table'} ${cssClasses}`}
          >
            {renderHeader()}
            <TableBody className="custom-table-body">
              {rows.length > 0 ? (
                rows.map((row, rowIndex) => renderRow(row, rowIndex))
              ) : (
                <TableRow className="row-item row-item-disabled">
                  <TableCell
                    className="custom-table-body-cell"
                    colSpan={columns.length}
                    align="center"
                    sx={{ padding: '8px 0px' }}
                  >
                    {noDataContent || defaultNoDataContent}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

CustomTable.propTypes = {
  colsDef: PropTypes.array,
  colSorting: PropTypes.bool,
  rowData: PropTypes.array,
  cssClasses: PropTypes.string,
  rowClassName: PropTypes.string,
  rowClickDisabled: PropTypes.bool,
  rowClickHandler: PropTypes.func,
  specialDataField: PropTypes.string,
  specialColumnType: PropTypes.string,
  sortableField: PropTypes.string,
  noDataContent: PropTypes.node,
  dark: PropTypes.bool,
  enableInfiniteScroll: PropTypes.bool,
  recordsPerPage: PropTypes.number,
};

export default CustomTable;
